import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash, CalendarBlank, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, fadeUp } from "../components/kit";

function HomeworkForm({ classes, onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState({ class_id: classes[0]?.id || "", subject: classes[0]?.subjects?.[0] || "", title: "", description: "", due_date: "", attachment_url: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const subs = classes.find((c) => c.id === f.class_id)?.subjects || [];
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/homework", f);
      toast.success("Homework posted — families notified");
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={t("Assign homework")} testid="homework-form-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("Class")}>
            <select className="field" value={f.class_id} onChange={(e) => { const c = classes.find((x) => x.id === e.target.value); setF({ ...f, class_id: e.target.value, subject: c?.subjects?.[0] || "" }); }} data-testid="hw-class-select">
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
            </select>
          </Field>
          <Field label={t("Subject")}>
            <select className="field" value={f.subject} onChange={set("subject")} data-testid="hw-subject-select">
              {subs.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t("Title")}><input className="field" required value={f.title} onChange={set("title")} data-testid="hw-title-input" /></Field>
        <Field label={t("Description")}><textarea rows={4} className="field" value={f.description} onChange={set("description")} data-testid="hw-desc-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("Due date")}><input type="date" className="field" value={f.due_date} onChange={set("due_date")} data-testid="hw-due-input" /></Field>
          <Field label="Attachment link"><input className="field" placeholder="https://…" value={f.attachment_url} onChange={set("attachment_url")} data-testid="hw-link-input" /></Field>
        </div>
        <button className="btn-primary w-full" data-testid="hw-submit-btn">{t("Assign homework")}</button>
      </form>
    </Modal>
  );
}

export default function Homework() {
  const { user } = useAuth();
  const { t } = useT();
  const staff = ["admin", "teacher"].includes(user.role);
  const { data: classes } = useApi(staff ? "/classes" : null);
  const [cls, setCls] = useState("");
  const { data, loading, reload } = useApi(`/homework?class_id=${cls}`);
  const [form, setForm] = useState(false);
  const del = async (h) => {
    if (!window.confirm("Delete this homework?")) return;
    try {
      await api.delete(`/homework/${h.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Assignments" title={t("Homework")}
        actions={staff && <>
          <select className="field !w-48 !rounded-full" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="hw-class-filter">
            <option value="">{t("All classes")}</option>
            {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
          </select>
          <button className="btn-primary" disabled={!classes?.length} onClick={() => setForm(true)} data-testid="assign-homework-btn"><Plus size={16} weight="bold" /> {t("Assign homework")}</button>
        </>} />
      {loading ? <Loader /> : !data?.length ? <GlassCard><Empty /></GlassCard> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {data.map((h, i) => {
            const overdue = h.due_date && h.due_date < new Date().toISOString().slice(0, 10);
            return (
              <motion.div key={h.id} {...fadeUp(i)}>
                <GlassCard hover className="p-6 h-full flex flex-col" data-testid={`homework-card-${h.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="rounded-full bg-blue-500/15 border border-blue-500/30 px-3 py-1 text-xs font-semibold text-blue-200">{h.subject}</span>
                    {staff && (user.role === "admin" || h.created_by === user.id) && (
                      <button className="btn-ghost !p-2 !text-rose-300" onClick={() => del(h)} data-testid={`hw-delete-${h.id}`}><Trash size={15} /></button>
                    )}
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{h.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-zinc-400 whitespace-pre-line">{h.description}</p>
                  {h.attachment_url && <a href={h.attachment_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-blue-300"><LinkIcon size={14} /> Attachment</a>}
                  <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4 text-xs">
                    <span className="text-zinc-500">{h.class_name} · {h.created_by_name}</span>
                    <span className={`inline-flex items-center gap-1 ${overdue ? "text-zinc-500" : "text-orange-300"}`}><CalendarBlank size={14} /> {fmtDate(h.due_date)}</span>
                  </div>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      )}
      {form && <HomeworkForm classes={classes} onClose={() => setForm(false)} onSaved={() => { setForm(false); reload(); }} />}
    </div>
  );
}
